/**
 * 列表查询会话：当前页面生命周期内记住 applied 筛选 / 分页 / 滚动。
 * 只放内存：SPA 进详情/换模块可恢复；浏览器大刷、关标签页后回到默认。
 */

const LIST_VIEW_SNAPSHOT_VERSION = 1;

/** 筛选 / 分页 / 表体滚动均已打开。 */
export const LIST_VIEW_RESTORE = {
  pagination: true,
  scroll: true,
} as const;

export const LIST_VIEW_KEYS = {
  schedule: 'schedule',
  dashboardOverview: 'dashboard/overview',
  dashboardReport: 'dashboard/report',
  monitorRisk: 'dashboard/monitor/risk',
  monitorMatrix: 'dashboard/monitor/matrix',
  milestoneTemplates: 'config/templates',
  milestoneNodes: 'config/nodes',
  autoCompleteRules: 'config/autoCompleteRules',
  monitorNodeConfig: 'calendar/monitor-node',
} as const;

export type ListViewScrollPos = {
  top: number;
  left: number;
};

export type ListViewSnapshot<TFilters> = {
  v: typeof LIST_VIEW_SNAPSHOT_VERSION;
  applied: TFilters;
  page: number;
  pageSize: number;
  scroll?: ListViewScrollPos;
};

const memoryCache = new Map<string, ListViewSnapshot<unknown>>();
const scrollWriteBlockedUntil = new Map<string, number>();
const pendingScrollWrites = new Map<string, ListViewScrollPos>();

function isScrollWriteBlocked(listViewKey: string): boolean {
  const until = scrollWriteBlockedUntil.get(listViewKey);
  if (until == null) return false;
  if (Date.now() >= until) {
    scrollWriteBlockedUntil.delete(listViewKey);
    return false;
  }
  return true;
}

/** 查询/重置/翻页后短时禁止把旧表体滚动写回快照。 */
export function blockListViewScrollWrite(listViewKey: string, durationMs = 320): void {
  scrollWriteBlockedUntil.set(listViewKey, Date.now() + durationMs);
}

export function takePendingListViewScroll(listViewKey: string): ListViewScrollPos | undefined {
  const pending = pendingScrollWrites.get(listViewKey);
  pendingScrollWrites.delete(listViewKey);
  return pending;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isValidSnapshot<TFilters>(value: unknown): value is ListViewSnapshot<TFilters> {
  if (!isPlainObject(value)) return false;
  if (value.v !== LIST_VIEW_SNAPSHOT_VERSION) return false;
  if (!isPlainObject(value.applied)) return false;
  if (typeof value.page !== 'number' || !Number.isFinite(value.page) || value.page < 1) return false;
  if (typeof value.pageSize !== 'number' || !Number.isFinite(value.pageSize) || value.pageSize < 1) {
    return false;
  }
  if (value.scroll != null) {
    if (!isPlainObject(value.scroll)) return false;
    if (typeof value.scroll.top !== 'number' || typeof value.scroll.left !== 'number') return false;
  }
  return true;
}

/** 用默认筛选的 key 收敛旧快照，缺字段回退默认值。 */
export function mergeListViewFilters<TFilters extends Record<string, unknown>>(
  defaults: TFilters,
  stored: unknown,
): TFilters {
  if (!isPlainObject(stored)) return cloneJson(defaults);
  const next = cloneJson(defaults);
  (Object.keys(defaults) as Array<keyof TFilters>).forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(stored, key as string)) return;
    try {
      next[key] = cloneJson(stored[key as string]) as TFilters[keyof TFilters];
    } catch {
      // 保留默认值
    }
  });
  return next;
}

export function readListViewSnapshot<TFilters>(listViewKey: string): ListViewSnapshot<TFilters> | null {
  const cached = memoryCache.get(listViewKey);
  if (cached && isValidSnapshot<TFilters>(cached)) {
    return cloneJson(cached) as ListViewSnapshot<TFilters>;
  }
  return null;
}

export function writeListViewSnapshot<TFilters>(
  listViewKey: string,
  snapshot: ListViewSnapshot<TFilters>,
): void {
  const next: ListViewSnapshot<TFilters> = {
    v: LIST_VIEW_SNAPSHOT_VERSION,
    applied: cloneJson(snapshot.applied),
    page: snapshot.page,
    pageSize: snapshot.pageSize,
    ...(snapshot.scroll ? { scroll: { ...snapshot.scroll } } : {}),
  };
  memoryCache.set(listViewKey, next as ListViewSnapshot<unknown>);
}

/** 只更新滚动。尚无快照时先记 pending，等列表会话写入后再合并。0,0 则删除 scroll 字段。 */
export function writeListViewScroll(listViewKey: string, scroll: ListViewScrollPos | null): void {
  if (isScrollWriteBlocked(listViewKey)) return;
  const current = readListViewSnapshot(listViewKey);
  if (!current) {
    if (scroll != null && (scroll.top > 0 || scroll.left > 0)) {
      pendingScrollWrites.set(listViewKey, {
        top: Math.max(0, scroll.top),
        left: Math.max(0, scroll.left),
      });
    }
    return;
  }
  const normalized = scroll == null
    ? null
    : {
        top: Math.max(0, Number.isFinite(scroll.top) ? scroll.top : 0),
        left: Math.max(0, Number.isFinite(scroll.left) ? scroll.left : 0),
      };
  const omit = normalized == null || (normalized.top === 0 && normalized.left === 0);
  writeListViewSnapshot(listViewKey, {
    v: current.v,
    applied: current.applied,
    page: current.page,
    pageSize: current.pageSize,
    ...(omit ? {} : { scroll: normalized }),
  });
}

export function clearAllListViewSnapshots(): void {
  memoryCache.clear();
  pendingScrollWrites.clear();
  scrollWriteBlockedUntil.clear();
}
